import { useState, type FormEvent } from 'react';

const FORM_ID = 'kangua-contact';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahbo1bp14a1h3mr6hvg';

type Status = 'idle' | 'loading' | 'success' | 'error';

const ContactForm = () => {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('contact_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }
    formData.delete('contact_alt');

    setStatus('loading');
    setFormError('');

    try {
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === 'string') payload.append(key, value);
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          isSpam || !response.ok
            ? serverMsg || '提交未通过校验，请检查后重试。'
            : serverMsg || '提交失败，请稍后重试。'
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，未能提交，请稍后重试。');
    }
  };

  const inputClass =
    'w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200';

  return (
    <form id={FORM_ID} data-readdy-form onSubmit={handleSubmit} className="w-full" noValidate>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-xs text-foreground-600">
            称呼
          </label>
          <input
            id="contact-name"
            type="text"
            name="name"
            required
            placeholder="怎么称呼你"
            autoComplete="name"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-xs text-foreground-600">
            邮箱
          </label>
          <input
            id="contact-email"
            type="email"
            name="email"
            required
            placeholder="用于回复你的邮箱"
            autoComplete="email"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-type" className="mb-1.5 block text-xs text-foreground-600">
          事项类型
        </label>
        <select id="contact-type" name="type" defaultValue="" className={inputClass}>
          <option value="">请选择</option>
          <option value="爆料线索">提供爆料线索</option>
          <option value="内容纠错">内容纠错</option>
          <option value="转载授权">转载与授权</option>
          <option value="商务合作">商务合作</option>
          <option value="其他建议">其他建议</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-message" className="mb-1.5 block text-xs text-foreground-600">
          具体内容（最多 500 字）
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述你的事项，涉及具体爆料线索请尽量提供可核实的时间与场景。"
          className={`${inputClass} resize-none`}
        ></textarea>
      </div>

      <input
        type="text"
        name="contact_alt"
        className="field-aux-offset"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          disabled={status === 'loading'}
          className="whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {status === 'loading' ? '提交中…' : '提交给编辑团队'}
        </button>
        <p className="text-xs text-foreground-500">
          我们会在 3 个工作日内回复，涉及线索核实可能需要更长时间。
        </p>
      </div>

      <div className="mt-3 min-h-[20px] text-sm" aria-live="polite">
        {status === 'success' && (
          <p className="flex items-center gap-1.5 text-accent-700">
            <i className="ri-checkbox-circle-line text-base"></i>
            已收到，感谢你的信息，编辑团队会尽快处理。
          </p>
        )}
        {status === 'error' && (
          <p className="flex items-center gap-1.5 text-primary-600">
            <i className="ri-error-warning-line text-base"></i>
            {formError || '提交失败，请稍后重试。'}
          </p>
        )}
      </div>
    </form>
  );
};

export default ContactForm;